/**
 * Copyright 2018 Phenix Inc. All Rights Reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

define([
    'phenix-web-lodash-light'
], function(_) {
    'use strict';

    function Schema(proto) {
        this._rawProtoJson = proto;
        this._parsedProto = {};
        this._enums = {};

        // First define all messages and enums
        mapProto.call(this, this._rawProtoJson, this._parsedProto);
        // Then define all fields and link types to messages or enums
        mapFields.call(this, this._rawProtoJson, this._parsedProto);
    }

    Schema.prototype.read = function read(type, pbf, end) {
        if (!this._parsedProto.messages[type]) {
            throw new Error('Unsupported proto type [' + type + ']');
        }

        return readFieldWithParent.call(this, this._parsedProto, type, pbf, end);
    };

    Schema.prototype.write = function write(type, obj, pbf) {
        if (!this._parsedProto.messages[type]) {
            throw new Error('Unsupported proto type [' + type + ']');
        }

        return writeFieldWithParent.call(this, this._parsedProto, type, obj, pbf);
    };

    function readFieldWithParent(parent, type, pbf, end) {
        var that = this;
        var response = buildDefaultObject(parent.messages[type]);

        return pbf.readFields(function(fieldId, result, pbfResult) {
            if (!parent.messages[type].fieldIds[fieldId]) {
                return;
            }

            var field = parent.messages[type].fields[fieldId];

            if (field.rule === 'repeated') {
                result[field.name].push(readField.call(that, parent.messages[type], type, fieldId, pbfResult));
            } else {
                result[field.name] = readField.call(that, parent.messages[type], type, fieldId, pbfResult);
            }
        }, response, end);
    }

    function writeFieldWithParent(parent, type, obj, pbf) {
        var that = this;
        var message = parent.messages[type];

        _.forEach(_.keys(message.fields), function(fieldId) {
            return writeField.call(that, parent, type, parseInt(fieldId), obj, pbf);
        });
    }

    function writeField(parent, type, fieldId, obj, pbf, index) {
        var that = this;
        var message = parent.messages[type];
        var field = message.fields[fieldId];
        var value = obj[field.name];

        if (!_.isUndefined(index)) {
            value = value[index];
        }

        if (field.defaultValue === value || _.isUndefined(value)) {
            return;
        }

        if (_.isArray(value)) {
            if (field.rule !== 'repeated') {
                throw new Error('Invalid type. Array is not valid with rule [' + field.rule + ']');
            }

            return _.forEach(value, function(item, idx) {
                writeField.call(that, parent, type, fieldId, obj, pbf, idx);
            });
        }

        if (_.isObject(field.schema) && _.isObject(value)) {
            return pbf.writeMessage(fieldId, _.bind(writeFieldWithParent, this, field.schema.parent, field.type), value);
        }

        var valueToWrite = _.isUndefined(value) ? field.defaultValue : value;

        // Enum
        if (_.isObject(field.schema) && !field.schema.fields) {
            var enumId = _.isNumber(value) ? value : _.get(field.schema, [valueToWrite, 'id']);

            return pbf.writeVarintField(fieldId, enumId);
        }

        switch (field.type) {
        case 'string':
            return pbf.writeStringField(fieldId, valueToWrite);
        case 'float':
            return pbf.writeFloatField(fieldId, valueToWrite);
        case 'double':
            return pbf.writeDoubleField(fieldId, valueToWrite);
        case 'bool':
            return pbf.writeBooleanField(fieldId, valueToWrite);
        case 'enum':
        case 'uint32':
        case 'uint64':
        case 'int32':
        case 'int64':
            return pbf.writeVarintField(fieldId, valueToWrite);
        case 'sint32':
        case 'sint64':
            return pbf.writeSVarintField(fieldId, valueToWrite);
        case 'fixed32':
            return pbf.writeFixed32Field(fieldId, valueToWrite);
        case 'fixed64':
            return pbf.writeFixed64Field(fieldId, valueToWrite);
        case 'sfixed32':
            return pbf.writeSFixed32Field(fieldId, valueToWrite);
        case 'sfixed64':
            return pbf.writeSFixed64Field(fieldId, valueToWrite);
        case 'bytes':
            return pbf.writeBytesField(fieldId, valueToWrite);
        default:
            throw new Error('Unexpected type: ' + field.type);
        }
    }

    Schema.prototype.getDefaults = function getDefaults() {

    };

    function mapProto(proto, parent) {
        if (_.get(proto, ['messages', 'length'], 0) > 0) {
            mapMessages.call(this, proto.messages, parent);
        }

        if (_.get(proto, ['enums', 'length'], 0) > 0) {
            mapEnums.call(this, proto.enums, parent);
        }
    }

    function mapFields(proto, parent) {
        var that = this;

        _.forEach(proto.messages, function(message) {
            parent.messages[message.name].fields = _.reduce(message.fields, _.bind(mapField, this, parent.messages[message.name]), {});

            if (message.messages) {
                mapFields.call(that, message, parent.messages[message.name]);
            }
        });
    }

    function mapMessages(messages, parent) {
        var that = this;

        parent.messages = _.reduce(messages, function(parsedMessages, message) {
            if (parent[message.name]) {
                return;
            }

            var fieldIds = _.reduce(message.fields, function(candidateIds, field) {
                candidateIds[field.id] = true;

                return candidateIds;
            }, {});

            parsedMessages[message.name] = {
                parent: parent,
                type: message.name,
                fieldIds: fieldIds
            };

            mapProto.call(that, message, parsedMessages[message.name]);

            return parsedMessages;
        }, {});
    }

    function mapEnums(enumsProto, parent) {
        parent.enums = _.reduce(enumsProto, function(enums, enumProto) {
            enums[enumProto.name] = _.reduce(enumProto.values, function(values, value) {
                values[value.name] = value;

                return values;
            }, {});

            return enums;
        }, {});
    }

    function mapField(parent, fieldObject, field) {
        // TODO(dy) add oneof property validation

        fieldObject[field.id] = {
            rule: field.rule,
            type: field.type,
            name: field.name,
            schema: mapType.call(this, field.type, parent),
            defaultValue: getDefaultValue(field.rule, field.type, _.get(field, ['options', 'default']))
        };

        return fieldObject;
    }

    function mapType(type, parent) {
        if (_.get(parent, ['messages', type])) {
            return parent.messages[type];
        }

        if (_.get(parent, ['enums', type])) {
            return parent.enums[type];
        }

        if (parent.parent) {
            return mapType(type, parent.parent);
        }

        return type;
    }

    function readField(message, type, fieldId, pbf) {
        var signed = type === 'int32' || type === 'int64' ? 'true' : '';
        var field = message.fields[fieldId];

        if (_.isObject(field.schema)) {
            // Field schema is a message
            if (field.schema.fields) {
                return readFieldWithParent.call(this, field.schema.parent, field.type, pbf, pbf.readVarint() + pbf.pos);
            }

            var enumId = pbf.readVarint(signed);
            var enumName = _.getEnumName(field.schema, enumId);

            if (_.isNullOrUndefined(enumName)) {
                throw new Error('Unsupported enum id [' + enumId + '] for field [' + field.name + '] on message [' + message.type + ']');
            }

            // Field schema is an Enum
            return enumName;
        }

        switch (field.type) {
        case 'string':
            return pbf.readString(signed);
        case 'float':
            return pbf.readFloat(signed);
        case 'double':
            return pbf.readDouble(signed);
        case 'bool':
            return pbf.readBoolean(signed);
        case 'enum':
            return pbf.readVarint(signed);
        case 'uint32':
        case 'uint64':
        case 'int32':
        case 'int64':
            return pbf.readVarint(signed);
        case 'sint32':
        case 'sint64':
            return pbf.readSVarint(signed);
        case 'fixed32':
            return pbf.readFixed32(signed);
        case 'fixed64':
            return pbf.readFixed64(signed);
        case 'sfixed32':
            return pbf.readSFixed32(signed);
        case 'sfixed64':
            return pbf.readSFixed64(signed);
        case 'bytes':
            return pbf.readBytes(signed);
        default:
            throw new Error('Unexpected type: ' + field.type);
        }
    }

    function getDefaultValue(rule, type, optionalDefaultValue) {
        if (rule === 'repeated') {
            return [];
        }

        switch (type) {
        case 'float':
        case 'double':
        case 'uint32':
        case 'uint64':
        case 'int32':
        case 'int64':
        case 'sint32':
        case 'sint64':
        case 'fixed32':
        case 'fixed64':
        case 'sfixed32':
        case 'sfixed64':
        case 'string':
        case 'enum':
        case 'bool':
        case 'long':
        default:
            return _.isUndefined(optionalDefaultValue) ? null : optionalDefaultValue;
        }
    }

    function buildDefaultObject(message) {
        return _.reduce(message.fields, function(defaultObject, field) {
            defaultObject[field.name] = _.clone(field.defaultValue);

            return defaultObject;
        }, {});
    }

    return Schema;
});